import React, { useEffect, useState, useMemo } from 'react'
import {
    Card,
    CardHeader,
    Container,
    Row,
    Button,
    Col,
    CardBody,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    CardTitle,
    Progress,
} from "reactstrap";
import { useDispatch, useSelector, connect } from 'react-redux';
import classnames from 'classnames';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import DatCourses from '../pages/DatCourses'
import { fetchTests } from 'redux/QuestionBank/TestProgressAction';
import { fetchSubjects } from 'redux/QuestionBank/SubjectsUnitsAction';
import { fetchQuestions } from 'redux/QuestionBank/QuestionsAction';

const QuantitativeReasoning = (props) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const SUBJECT_NAME = "Quantitative Reasoning";
    const BTN_COLOR = "warning";
    const GRADIENT = "bg-gradient-warning";
    const ACTIVE_TAB_BG = "#fb6340";
    const HOVER_COLOR = "orange";

    const topics = {
        "dat-biology" : "Biology",
        "dat-general-chemistry" : "General Chemistry",
        "dat-organic-chemistry" : "Organic Chemistry",
        "dat-perceptual-ability" : "Perceptual Ability",
        "dat-reading-comprehension" : "Reading Comprehension",
        "dat-quantitative-reasoning" : "Quantitative Reasoning",
    }

    // QB Redux state
    const { tests } = useSelector(state => state.testProgress);
    const { subjects: qbSubjects } = useSelector(state => state.questionBankSubjects);
    const { questions: qbQuestions } = useSelector(state => state.questionBankQuestions);

    useEffect(() => {
        dispatch(fetchTests());
        dispatch(fetchSubjects());
        dispatch(fetchQuestions());
    }, []);

    // Find the QB subjectId that matches this page's subject name
    const subjectId = useMemo(() => {
        if (!qbSubjects?.length) return null;
        const match = qbSubjects.find(s => s.name && s.name.toLowerCase().includes(SUBJECT_NAME.toLowerCase()));
        return match?.subjectId || null;
    }, [qbSubjects]);

    // Calculate stats for this subject from completed tests
    const subjectStats = useMemo(() => {
        const completedTests = tests?.filter(t => t.status === 'completed') || [];
        const availableQuestions = qbQuestions?.filter(q => q.subjectId === subjectId)?.length || 0;

        if (!subjectId) {
            return { correct: 0, incorrect: 0, omitted: 0, totalAnswered: 0, testsWithSubject: 0, questionsUsed: 0, availableQuestions: 0, correctPercent: 0 };
        }

        let correct = 0;
        let incorrect = 0;
        let omitted = 0;
        let testsWithSubject = 0;
        const uniqueQuestionsUsed = new Set();

        completedTests.forEach(test => {
            if (!test.testQuestions) return;
            let hasSubjectQuestion = false;

            test.testQuestions.forEach(question => {
                if (question.subjectId !== subjectId) return;
                hasSubjectQuestion = true;
                uniqueQuestionsUsed.add(question.questionId);

                const selectedAnswerId = test.selectedAnswers?.[question.questionId];
                const correctAnswer = question.answers?.find(a => a.isCorrect);
                const isCorrect = selectedAnswerId === correctAnswer?.answerId;
                const wasOmitted = !selectedAnswerId;

                if (wasOmitted) omitted++;
                else if (isCorrect) correct++;
                else incorrect++;
            });

            if (hasSubjectQuestion) testsWithSubject++;
        });

        const totalAnswered = correct + incorrect + omitted;
        const correctPercent = totalAnswered > 0 ? Math.round((correct / totalAnswered) * 100) : 0;

        return {
            correct,
            incorrect,
            omitted,
            totalAnswered,
            testsWithSubject,
            questionsUsed: uniqueQuestionsUsed.size,
            availableQuestions,
            correctPercent
        };
    }, [tests, qbQuestions, subjectId]);

    const [activeTab, setActiveTab] = useState('1');

    const toggle = tab => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const [hover, setHover] = useState(false);

    const activeTabStyle = { color: "white" , backgroundColor: ACTIVE_TAB_BG }
    const inactiveTabStyle = { color: hover ? HOVER_COLOR : '' }

    const handleMouseEnter = () => {
        setHover(true);
    };

    const handleMouseLeave = () => {
        setHover(false);
    };

    return (
    <>
        <div className={`header ${GRADIENT} pb-8 pt-5 pt-md-8`}>
            <Container fluid>
                <div className="header-body">

                </div>
            </Container>
        </div>

            <Container className="mt--7" fluid>
                <Row>
                    <Col md="1"></Col>
                    <Col md="10">
                        <Card className=''>
                            <CardHeader className="font-weight-bolder text-xl text-default">{topics[props.location.pathname.split("/")[2]]}</CardHeader>
                        </Card>
                    </Col>
                    <Col md="1"></Col>

                    <Col md="1"></Col>
                    <Col md="10 mt-5 mb-3">
                        <Nav tabs>
                            <NavItem className="font-weight-bold">
                                <NavLink
                                    style={ activeTab === '1' ? activeTabStyle : inactiveTabStyle}
                                    className={classnames({ active: activeTab === '1'})}
                                    onClick={() => toggle('1')}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    Question Banks
                                </NavLink>
                            </NavItem>
                            <NavItem className="font-weight-bold">
                                <NavLink
                                    style={activeTab === '2' ? activeTabStyle : inactiveTabStyle}
                                    className={classnames({ active: activeTab === '2' })}
                                    onClick={() => toggle('2')}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    Video Content
                                </NavLink>
                            </NavItem>
                        </Nav>
                    </Col>
                    <Col md="1"></Col>
                    <Col md="1"></Col>
                    <Col md="10">
                        <Card >
                            <CardBody className='mt-2 pt-2'>

                                <TabContent activeTab={activeTab}>
                                    <TabPane tabId="1">
                                        <Row>
                                            <Col sm="12">
                                                <p className='text-lg text-default font-weight-bolder mb-4'>Question Bank Overview</p>
                                            </Col>
                                            {subjectStats.totalAnswered > 0 ? (
                                            <>
                                                <Col lg="4" md="6" className='mb-3'>
                                                    <Card className="card-stats h-100">
                                                        <CardBody>
                                                            <Row>
                                                                <Col className="col-auto">
                                                                    <div className={`icon icon-shape bg-${BTN_COLOR} text-white rounded-circle shadow`}>
                                                                        <i className="fas fa-question-circle" />
                                                                    </div>
                                                                </Col>
                                                                <div className="col">
                                                                    <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                                                                        Questions Used
                                                                    </CardTitle>
                                                                    <span className="h2 font-weight-bold mb-0">
                                                                        {subjectStats.questionsUsed} / {subjectStats.availableQuestions}
                                                                    </span>
                                                                </div>
                                                            </Row>
                                                            <div className="mt-3">
                                                                <Progress
                                                                    value={subjectStats.availableQuestions > 0 ? Math.round((subjectStats.questionsUsed / subjectStats.availableQuestions) * 100) : 0}
                                                                    color={BTN_COLOR}
                                                                    className="progress-sm"
                                                                />
                                                            </div>
                                                        </CardBody>
                                                    </Card>
                                                </Col>
                                                <Col lg="4" md="6" className='mb-3'>
                                                    <Card className="card-stats h-100">
                                                        <CardBody>
                                                            <Row>
                                                                <Col className="col-auto">
                                                                    <div className={`icon icon-shape bg-success text-white rounded-circle shadow`}>
                                                                        <i className="fas fa-check-circle" />
                                                                    </div>
                                                                </Col>
                                                                <div className="col">
                                                                    <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                                                                        Correct Rate
                                                                    </CardTitle>
                                                                    <span className="h2 font-weight-bold mb-0">
                                                                        {subjectStats.correctPercent}%
                                                                    </span>
                                                                </div>
                                                            </Row>
                                                            <p className="mt-3 mb-0 text-muted text-sm">
                                                                <span className="text-success mr-2"><i className="fas fa-check"></i> {subjectStats.correct}</span>
                                                                <span className="text-danger mr-2"><i className="fas fa-times"></i> {subjectStats.incorrect}</span>
                                                                <span className="text-warning"><i className="fas fa-minus-circle"></i> {subjectStats.omitted}</span>
                                                            </p>
                                                        </CardBody>
                                                    </Card>
                                                </Col>
                                                <Col lg="4" md="6" className='mb-3'>
                                                    <Card className="card-stats h-100">
                                                        <CardBody>
                                                            <Row>
                                                                <Col className="col-auto">
                                                                    <div className={`icon icon-shape bg-${BTN_COLOR} text-white rounded-circle shadow`}>
                                                                        <i className="fas fa-file-alt" />
                                                                    </div>
                                                                </Col>
                                                                <div className="col">
                                                                    <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                                                                        Tests Taken
                                                                    </CardTitle>
                                                                    <span className="h2 font-weight-bold mb-0">
                                                                        {subjectStats.testsWithSubject}
                                                                    </span>
                                                                </div>
                                                            </Row>
                                                        </CardBody>
                                                    </Card>
                                                </Col>
                                            </>
                                            ) : (
                                                <Col sm="12" className="text-center mb-4">
                                                    <div className="py-4">
                                                        <i className={`fas fa-chart-bar text-${BTN_COLOR} mb-3`} style={{fontSize: '3rem', opacity: 0.5}}></i>
                                                        <h3 className="text-muted">No data yet</h3>
                                                        <p className="text-muted">Start practicing to see your {SUBJECT_NAME} stats here.</p>
                                                    </div>
                                                </Col>
                                            )}
                                            <Col sm="12" className='mt-3'>
                                                <Button
                                                    color={BTN_COLOR}
                                                    className="mr-3"
                                                    onClick={() => history.push(`/client/question-bank/create-test${subjectId ? `?subject=${subjectId}` : ''}`)}
                                                >
                                                    <i className="fas fa-plus-circle mr-2"></i>Go to Question Bank
                                                </Button>
                                                <Button
                                                    color={BTN_COLOR}
                                                    outline
                                                    onClick={() => history.push('/client/question-bank/performance')}
                                                >
                                                    <i className="fas fa-chart-line mr-2"></i>View Full Performance
                                                </Button>
                                            </Col>
                                        </Row>
                                    </TabPane>
                                    <TabPane tabId="2" className='mt-2'>
                                        <DatCourses bgColor={`bg-${BTN_COLOR}`} textColor="text-white" subject={topics[props.location.pathname.split("/")[2]]}/>
                                    </TabPane>
                                </TabContent>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md="1"></Col>
                </Row>
            </Container>
    </>
  )
}
const mapStateToProps = state => {
    return {
      userDetails : state.loginAuth.userDetails,
    }
}

export default connect(mapStateToProps)(QuantitativeReasoning)
